import React from 'react';

import { makeStyles } from '@material-ui/core/styles';

import { Criterium, CriteriumTable } from '@hydra/common/criterium';
import WorldMap from '@hydra/common/components/WorldMap';
import { useReviewContext } from '@hydra/common/context';
import { Icon, Flag, Translate, SafeAnchor } from '@hydra/common/components';

import getWooIndexLink from '@hydra/utils/getWooIndexLink';

import { useI18n } from '@hydra/common/customHooks';

import { CriteriumInterface } from '@hydra/types/review';

const COUNTRIES_TOTAL_SHARE = 1;

const useStyles = makeStyles({
  textCountry: {
    verticalAlign: 'text-bottom'
  }
});

interface TopCountry {
  country: string
  value: number
  change: number
}

interface TrafficTopCountries extends CriteriumInterface {
  data: {
    countries: TopCountry[]
  }
}

const Delta = ({ change }: { change: number }) => {
  const { numeral } = useI18n();
  const parsedData = change === 0 ? change : numeral(Math.abs(change)).format('(0.00%)');

  if (change > 0) {
    return (
      <span>
        {parsedData}
        <Icon type='triangle-up' color='success' size='xs' />
      </span>
    );
  }
  if (change < 0) {
    return (
      <span>
        {parsedData}
        <Icon type='triangle-down' color='error' size='xs' />
      </span>
    );
  }

  return (
    <span>
      {parsedData}
      <Icon type='blank' color='grey' size='xs' />
    </span>
  );
};

export const TrafficTopCountries = ({ criterium }: { criterium: TrafficTopCountries }) => {
  const classes = useStyles();
  const { isPdf } = useReviewContext();
  const { numeral } = useI18n();

  const head = [
    <Translate content='traffic_top_countries_country' key='country' />,
    <Translate content='traffic_top_countries_value' key='value' />,
    <Translate content='traffic_top_countries_change' key='change' />
  ];

  const sortedCountries = criterium.data.countries.sort((countryA, countryB) => {
    if (countryA.value < countryB.value) { return 1; }
    if (countryA.value > countryB.value) { return -1; }
    return 0;
  });

  const countriesTotalShare = sortedCountries.reduce((total, country) => {
    total = total + country.value;
    return total;
  }, 0);

  const allCountries = [...sortedCountries];
  if (COUNTRIES_TOTAL_SHARE - countriesTotalShare > 0.01) {
    allCountries.push({
      country: '',
      value: COUNTRIES_TOTAL_SHARE - countriesTotalShare,
      change: 0
    });
  }

  const tableClasses = {
    head: ['alignLeft', 'alignRight', 'alignRight'],
    body: ['alignLeft', 'alignRight', 'alignRight']
  };

  const rows = allCountries.map(country => {
    const countryCode = country.country.trim();

    const countryName = country.country === ''
      ? (
        <React.Fragment key='flag'>
          <span>
            <Icon type='domain' size='sm' />
          </span>
          &nbsp;
          <Translate content='traffic_top_countries_others' className={classes.textCountry} />
        </React.Fragment>
        )
      : (
        <React.Fragment key='flag'>
          <Flag country={countryCode.toLowerCase()} />
          &nbsp;
          {
            isPdf
              ? <Translate content={`country_name_${countryCode.toUpperCase()}`} />
              : (
                <SafeAnchor href={getWooIndexLink(countryCode)} rel='nofollow noopener noreferrer' target='_blank'>
                  <Translate content={`country_name_${countryCode.toUpperCase()}`} />
                </SafeAnchor>
                )
          }
        </React.Fragment>
        );
    return [
      countryName,
      <span key='value'>{numeral(country.value).format('(0.00%)')}</span>,
      <span key='change'><Delta change={country.change} /></span>
    ];
  });

  const content = (
    <>
      <WorldMap highlights={criterium.data.countries} />
      <CriteriumTable
        head={head}
        rows={rows}
        fixedColumns={[5, 2.5, 2.5]}
        thinHead
        classNames={tableClasses}
        striped
        nowrap
      />
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

export default React.memo(TrafficTopCountries);
